import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import PageHeader from "../components/common/PageHeader";
import LocationMapView from "../components/common/LocationMapView";
import LocationCompareModal from "../components/LocationCompareModal";
import LocationHistoryModal from "../components/LocationHistoryModal";
import LocationSpreadsheetView from "../components/LocationSpreadsheetView";
import ExportToGbpModal from "../components/ExportToGbpModal";
import BuildingMapModal from "../components/BuildingMapModal";
import CreateOrLinkMasterLocationModal from "../components/CreateOrLinkMasterLocationModal";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications";
import { useDebounce } from "../hook/useDebounce";
import { Icon } from "@iconify/react";

const LocationsMainListPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const activeAreaId = state?.filterAreaId || null;
  const activeAreaName = state?.filterAreaName || null;

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [compareLocationId, setCompareLocationId] = useState(null);
  const [buildingMapLocation, setBuildingMapLocation] = useState(null);
  const [historyEndpoint, setHistoryEndpoint] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportPreSelectedIds, setExportPreSelectedIds] = useState([]);
  const [createLinkModalOpen, setCreateLinkModalOpen] = useState(false);

  /* ===== View Mode Switcher State ===== */
  const [viewMode, setViewMode] = useState("table");

  /* ===== Sorting State ===== */
  const [sortConfig, setSortConfig] = useState({ key: "location_name", direction: "asc" });
  
  const debouncedSearch = useDebounce(search, 500);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/main-locations", {
        params: {
          search: debouncedSearch,
          page: (viewMode === "map" || viewMode === "sheet") ? 1 : page,
          limit: viewMode === "map" ? 100 : viewMode === "sheet" ? 100 : limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction,
          ...(activeAreaId && { top_location_id: activeAreaId }),
        },
      });

      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total ?? res.data?.count ?? res.data?.data?.length ?? 0);
    } catch {
      showToast("error", "Failed to load main locations");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, activeAreaId, sortConfig, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTogglePublishing = async (e, row, field) => {
    e.stopPropagation();
    const locId = row.location_id || row.id;
    const currentVal = Number(row[field]) === 1 ? 1 : 0;
    const nextVal = currentVal === 1 ? 0 : 1;

    setData((prev) =>
      prev.map((item) =>
        (item.location_id || item.id) === locId
          ? { ...item, [field]: nextVal }
          : item
      )
    );

    try {
      const res = await api.put(`/main-locations/${locId}`, {
        [field]: nextVal,
      });
      if (res.data?.version) {
        setData((prev) =>
          prev.map((item) =>
            (item.location_id || item.id) === locId
              ? { ...item, [field]: nextVal, version_number: res.data.version }
              : item
          )
        );
      }
    } catch {
      setData((prev) =>
        prev.map((item) =>
          (item.location_id || item.id) === locId
            ? { ...item, [field]: currentVal }
            : item
        )
      );
      showToast("error", "Failed to update publishing status");
    }
  };

  return (
    <>
      <PageHeader
        title="Master Data"
        subtitle="Manage core master business locations."
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary-600 radius-8 px-16 py-8 d-flex align-items-center gap-2 text-sm fw-semibold shadow-sm"
              onClick={() => setCreateLinkModalOpen(true)}
            >
              <Icon icon="solar:map-point-add-bold" className="text-base" />
              <span>Create / Link Location</span>
            </button>

            <button
              type="button"
              className="btn btn-outline-primary radius-8 px-16 py-8 d-flex align-items-center gap-2 text-sm fw-semibold shadow-sm"
              onClick={() => {
                setExportPreSelectedIds([]);
                setIsExportModalOpen(true);
              }}
            >
              <Icon icon="logos:google-icon" className="text-base" />
              <span>Export to Google Business</span>
            </button>

            <div className="btn-group radius-8 overflow-hidden border">
              <button
                className={`btn btn-sm ${viewMode === "table" ? "btn-primary" : "btn-light text-neutral-600"}`}
                onClick={() => setViewMode("table")}
              >
                <Icon icon="ri:table-line" className="me-1 text-base" /> Table
              </button>
              <button
                className={`btn btn-sm ${viewMode === "sheet" ? "btn-primary" : "btn-light text-neutral-600"}`}
                onClick={() => setViewMode("sheet")}
              >
                <Icon icon="ri:file-excel-2-line" className="me-1 text-base" /> Sheet View
              </button>
              <button
                className={`btn btn-sm ${viewMode === "map" ? "btn-primary" : "btn-light text-neutral-600"}`}
                onClick={() => setViewMode("map")}
              >
                <Icon icon="ri:map-pin-line" className="me-1 text-base" /> Map View
              </button>
            </div>
          </>
        }
      />

      {activeAreaId && (
        <div className="mb-12 px-10">
          <span
            className="badge bg-primary-100 text-primary-600 px-12 py-8 radius-8 pointer d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/locations-main", { replace: true })}
          >
            Filtered by Top Location: {activeAreaName} 
            <i className="ri-close-line"></i>
          </span>
        </div>
      )}

      {viewMode === "table" ? (
        <DataTable
          data={data}
          loading={loading}
          searchTerm={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => {
            setLimit(v);
            setPage(1);
          }}
          showCheckbox={false}
          sortConfig={sortConfig}
          onSortChange={(config) => {
            setSortConfig(config);
            setPage(1);
          }}

          headerActions={[
            {
              label: "Add Business",
              icon: "ri-add-line",
              className: "btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2",
              onClick: () => navigate("/locations-main/new"),
            },
          ]}

          storageKey="loqation_table_columns_master_v1"
          columns={[
            {
              key: "location_name",
              label: "Name",
              category: "General",
              defaultVisible: true,
              className: "text-xs fw-semibold",
              sortable: true,
              render: (row) => (
                <span
                  className="text-primary-600 pointer hover-text-decoration-underline"
                  onClick={() =>
                    navigate("/locations-sub", {
                      state: {
                        filterBusinessId: row.id,
                        filterBusinessName: row.location_name,
                      },
                    })
                  }
                >
                  {row.location_name}
                </span>
              ),
            },
            {
              key: "store_code",
              label: "Store Code",
              category: "General",
              defaultVisible: true,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.store_code || "—",
            },
            { 
              key: "top_location_name", 
              label: "Top Location", 
              category: "General",
              defaultVisible: true,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.top_location_name || "—",
            },
            { 
              key: "primary_category_name", 
              label: "Category", 
              category: "General",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.primary_category_name || "—",
            },
            { 
              key: "street_address", 
              label: "Street Address", 
              category: "Address",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.street_address || "—",
            },
            { 
              key: "postal_code", 
              label: "Postal Code", 
              category: "Address",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.postal_code || "—",
            },
            { 
              key: "city", 
              label: "City", 
              category: "Address",
              defaultVisible: true,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.city || "—",
            },
            { 
              key: "state", 
              label: "State / Region", 
              category: "Address",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.state || "—",
            },
            { 
              key: "country_code", 
              label: "Country", 
              category: "Address",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.country_code || "—",
            },
            { 
              key: "phone_number", 
              label: "Phone", 
              category: "Contact",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.phone_number || "—",
            },
            { 
              key: "email", 
              label: "Email", 
              category: "Contact",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.email || "—",
            },
            { 
              key: "website_url", 
              label: "Website", 
              category: "Contact",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => row.website_url ? (
                <a href={row.website_url} target="_blank" rel="noreferrer" className="text-primary-600 text-decoration-none text-xs">
                  Link ↗
                </a>
              ) : "—",
            },
            {
              key: "publish_google_business",
              label: "Publishing",
              category: "Publishing",
              defaultVisible: true,
              sortable: true,
              render: (row) => {
                const gbp = Number(row.publish_google_business) === 1;
                const apple = Number(row.publish_apple_business) === 1;
                return (
                  <div className="d-flex align-items-center gap-1 flex-nowrap">
                    <button
                      type="button"
                      onClick={(e) => handleTogglePublishing(e, row, "publish_google_business")}
                      className={`badge px-8 py-4 radius-4 text-2xs fw-semibold d-inline-flex align-items-center justify-content-center gap-1 pointer ${
                        gbp
                          ? "bg-success-50 text-success-700 border border-success-200"
                          : "bg-neutral-100 text-neutral-500 border border-neutral-200"
                      }`}
                      style={{
                        width: "92px",
                        height: "24px",
                        whiteSpace: "nowrap",
                        transition: "all 0.15s ease",
                      }}
                      title={
                        gbp
                          ? "Google Business Profile: Active (Click to turn off)"
                          : "Google Business Profile: Inactive (Click to turn on)"
                      }
                    >
                      <Icon
                        icon="logos:google-icon"
                        style={{ fontSize: "12px", opacity: gbp ? 1 : 0.4, flexShrink: 0 }}
                      />
                      <span>{gbp ? "Google On" : "Google Off"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleTogglePublishing(e, row, "publish_apple_business")}
                      className={`badge px-8 py-4 radius-4 text-2xs fw-semibold d-inline-flex align-items-center justify-content-center gap-1 pointer ${
                        apple
                          ? "bg-success-50 text-success-700 border border-success-200"
                          : "bg-neutral-100 text-neutral-500 border border-neutral-200"
                      }`}
                      style={{
                        width: "92px",
                        height: "24px",
                        whiteSpace: "nowrap",
                        transition: "all 0.15s ease",
                      }}
                      title={
                        apple
                          ? "Apple Business Connect: Active (Click to turn off)"
                          : "Apple Business Connect: Inactive (Click to turn on)"
                      }
                    >
                      <Icon
                        icon="ic:baseline-apple"
                        style={{ fontSize: "13px", opacity: apple ? 1 : 0.4, flexShrink: 0 }}
                      />
                      <span>{apple ? "Apple On" : "Apple Off"}</span>
                    </button>
                  </div>
                );
              },
            },
            {
              key: "sync_status",
              label: "Status",
              category: "System",
              defaultVisible: true,
              sortable: true,
              render: (row) => (
                <span
                  className={`badge px-8 py-2 radius-4 fw-medium text-xs ${
                    row.sync_status === "SYNCED"
                      ? "bg-success-focus text-success-main"
                      : "bg-warning-focus text-warning-main"
                  }`}
                >
                  {row.sync_status}
                </span>
              ),
            },
            { 
              key: "version_number", 
              label: "Version", 
              category: "System",
              defaultVisible: false,
              className: "text-xs text-secondary-light fw-medium",
              sortable: true,
              render: (row) => (
                <span className="badge bg-neutral-100 text-neutral-700 text-2xs px-8 py-4 radius-6">
                  v{row.version_number || 1}
                </span>
              ),
            },
          ]}

          actions={[
            {
              icon: "solar:buildings-2-bold",
              title: "Building Outline & Levels Map",
              className: "w-28-px h-28-px bg-warning-focus text-warning-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => setBuildingMapLocation(row),
            },
            {
              icon: "solar:cloud-upload-bold",
              title: "Export to Google Business Profile",
              className: "w-28-px h-28-px bg-primary-50 text-primary-600 rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => {
                if (Number(row.publish_google_business) !== 1) {
                  showToast(
                    "warning",
                    `"${row.location_name}" is not approved for Google Business Profile publishing. Enable it in the location's Publishing settings first.`
                  );
                  return;
                }
                setExportPreSelectedIds([row.location_id || row.id]);
                setIsExportModalOpen(true);
              },
            },
            {
              icon: "solar:scale-bold",
              title: "Compare Data Sources",
              className: "w-28-px h-28-px bg-info-focus text-info-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => setCompareLocationId(row.location_id || row.id),
            },
            {
              icon: "lucide:edit",
              className: "w-28-px h-28-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => navigate(`/locations-main/${row.location_id}/edit`),
            },
            {
              icon: "mingcute:delete-2-line",
              className: "w-28-px h-28-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              onClick: (row) => {
                confirmDelete(row.location_name, async () => {
                  try {
                    await api.delete(`/main-locations/${row.location_id}`);
                    showToast("success", "Main location deleted");
                    fetchData();
                  } catch {
                    showToast("error", "Delete failed");
                  }
                });
              },
            },
          ]}
        />
      ) : viewMode === "sheet" ? (
        <LocationSpreadsheetView
          data={data}
          loading={loading}
          onRefresh={fetchData}
          activeAreaId={activeAreaId}
          onOpenHistory={(locId) => setHistoryEndpoint(`/main-locations/${locId}/history`)}
          onOpenCompare={(locId) => setCompareLocationId(locId)}
        />
      ) : (
        <LocationMapView 
          locations={data} 
          loading={loading}
          allowPinDrag={true}
          onRefresh={fetchData}
          getViewRoute={(item) => `/locations-main/${item.location_id || item.id}/edit`} 
        />
      )}

      {/* BUILDING OUTLINE & LEVELS MAP OVERLAY */}
      <BuildingMapModal
        isOpen={Boolean(buildingMapLocation)}
        onClose={() => setBuildingMapLocation(null)}
        location={buildingMapLocation}
      />

      {/* 3-WAY DATA COMPARISON MODAL */}
      <LocationCompareModal
        isOpen={Boolean(compareLocationId)}
        onClose={() => setCompareLocationId(null)}
        locationId={compareLocationId}
      />

      {/* HISTORY AUDIT MODAL */}
      {historyEndpoint && (
        <LocationHistoryModal
          endpoint={historyEndpoint}
          onClose={() => setHistoryEndpoint(null)}
        />
      )}

      {/* EXPORT TO GOOGLE BUSINESS PROFILE MODAL */}
      <ExportToGbpModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        preSelectedIds={exportPreSelectedIds}
        onExportCompleted={fetchData}
      />

      {/* CREATE OR LINK LOCATION MODAL */}
      <CreateOrLinkMasterLocationModal
        isOpen={createLinkModalOpen}
        onClose={() => setCreateLinkModalOpen(false)}
        onSuccess={() => {
          fetchData();
        }}
      />
    </>
  );
};

export default LocationsMainListPage;